'use client';

import React, { useState, useEffect } from 'react';
import Logo from '@/UIComponents/Logo/Logo';
import InputField from '@/UIlibrary/InputField/InputField';
import Button from '@/UIlibrary/Button/Button';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useParams, useSearchParams } from 'react-router-dom';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import {
	useAcceptInvitationMutation,
	AcceptInvitationMutation,
	AcceptInvitationInput
} from '@/graphql/__generated__/authentication';
import { toast } from 'react-hot-toast';
import useAuthStore from '@/lib/auth';
import logger from '@/logger';
import LanguageSelector from '@/stories/Components/LanguageSelector/LanguageSelector';
import { validatePassword } from '@/utils/utils';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { jwtDecode } from 'jwt-decode';

export const PREVENT_REDIRECT_FLAG = '__STORYBOOK_PREVENT_ACCEPT_INVITATION_REDIRECT__';

interface AcceptInvitationErrors {
	password?: string;
	passwordConfirmation?: string;
}

const AcceptInvitationForm: React.FC = () => {
	const { lang } = useParams();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');

	const [formData, setFormData] = useState({
		password: '',
		passwordConfirmation: ''
	});
	const { setToken, isAuthenticated } = useAuthStore();
	const [formErrors, setFormErrors] = useState<AcceptInvitationErrors>({});
	const [formIsValid, setFormIsValid] = useState<boolean>(false);
	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const t = useTranslations('AcceptInvitation');

	const authenticationClient = useApolloClient(SCHEMAS.AUTHENTICATION);
	const [acceptInvitation, { loading: loadingAcceptInvitation }] = useAcceptInvitationMutation({
		client: authenticationClient,
		onCompleted: (data: AcceptInvitationMutation) => {
			const acceptData = data.acceptInvitation;
			if (acceptData?.successful) {
				logger.debug('Invitation accepted successfully');
				toast.success(t('InvitationAcceptedSuccess'), { duration: 15000 });
				setToken(null);

				let email = '';
				if (token) {
					try {
						const decoded = jwtDecode<{ email: string }>(token);
						email = decoded.email || '';
					} catch (error) {
						logger.error('Error decoding token for email:', error);
					}
				}

				const signInUrl = email
					? `/${lang}/signin?email=${encodeURIComponent(email)}`
					: `/${lang}/signin`;
				if (!(window as unknown as Record<string, boolean>)[PREVENT_REDIRECT_FLAG]) {
					window.location.href = signInUrl;
				}
			}
			if (acceptData?.mutationErrors && acceptData.mutationErrors.length > 0) {
				acceptData.mutationErrors.forEach((error) => {
					if (error) {
						switch (error.__typename) {
							case 'InvalidInput':
								setFormErrors((prev) => ({
									...prev,
									[error.field]: error.message
								}));
								break;
							case 'ActionFailed':
							case 'InvalidAction':
								toast.error(error.message || t('InvitationAcceptFailed'), { duration: 15000 });
								break;
							default:
								logger.debug('Unhandled mutation error in accept invitation', error);
								break;
						}
					}
				});
			}
		},
		onError: () => {
			toast.error(t('InvitationAcceptFailed'), { duration: 15000 });
		}
	});

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setShowConfirmModal(true);
	};

	const handleConfirmPassword = () => {
		setFormErrors({});
		const acceptInvitationInput: AcceptInvitationInput = {
			password: formData.password,
			passwordConfirmation: formData.passwordConfirmation
		};
		acceptInvitation({ variables: { input: acceptInvitationInput } });
		setShowConfirmModal(false);
	};

	useEffect(() => {
		if (isAuthenticated()) {
			setToken(null);
		}
	}, [isAuthenticated, setToken]);

	useEffect(() => {
		if (token) {
			setToken(token);
		}
	}, [token, setToken]);

	useEffect(() => {
		const passwordsMatch = formData.password === formData.passwordConfirmation;
		const noEmptyFields = Object.values(formData).every((value) => value !== '');
		const isPasswordValid = validatePassword(formData.password);

		setFormIsValid(noEmptyFields && passwordsMatch && isPasswordValid);

		setFormErrors((prev) => {
			const updated = { ...prev };

			if (!isPasswordValid && formData.password.length > 0) {
				updated.password = t('PasswordFormatDisclaimer');
			} else {
				delete updated.password;
			}

			if (!passwordsMatch && formData.passwordConfirmation.length > 0) {
				updated.passwordConfirmation = t('PasswordsDoNotMatch');
			} else {
				delete updated.passwordConfirmation;
			}

			return updated;
		});
	}, [formData, t]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prevFormData) => ({
			...prevFormData,
			[name]: value
		}));
	};

	return (
		<div className="w-full h-screen flex flex-col justify-center items-center">
			<div className="absolute top-4 right-4">
				<LanguageSelector />
			</div>
			<ConfirmModal
				isOpen={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				onCancel={() => setShowConfirmModal(false)}
				onConfirm={handleConfirmPassword}
				title={t('CreatePassword')}
				subtitle={t('SignInAfterPassword')}
				confirmActionText={t('CreatePassword')}
				confirmBtnLoading={loadingAcceptInvitation}
				maxWidth={600}
			/>
			<form
				className="flex flex-col w-11/12 md:w-full max-w-[550px] mx-auto bg-[#FFFFFF] py-12 px-11 rounded-[25px] gap-6"
				onSubmit={handleSubmit}
			>
				<div className="flex flex-row justify-center">
					<Logo src="/logo-white.jpg" width={300} height={300} />
				</div>
				<div className="flex flex-col gap-8">
					<div className="flex flex-col gap-3">
						<Typography type="display" size="sm" weight="semibold">
							{t('Title')}
						</Typography>
						<Typography color="subtitle" size="sm">
							{t('Description')}
						</Typography>
					</div>
					<div className="flex flex-col gap-2">
						<InputField
							name="password"
							type="password"
							label={t('PasswordLabel')}
							value={formData.password}
							onChange={handleChange}
							loading={loadingAcceptInvitation}
							size={'auto'}
							placeholder={t('PasswordPlaceholder')}
							hint={formErrors.password || t('PasswordFormatDisclaimer')}
							error={!!formErrors.password}
						/>
						<InputField
							name="passwordConfirmation"
							type="password"
							label={t('PasswordConfirmationLabel')}
							value={formData.passwordConfirmation}
							onChange={handleChange}
							loading={loadingAcceptInvitation}
							disabled={loadingAcceptInvitation}
							placeholder={t('PasswordConfirmationPlaceholder')}
							size={'auto'}
							hint={formErrors.passwordConfirmation}
							error={!!formErrors.passwordConfirmation}
						/>
						<Button
							type="submit"
							disabled={!formIsValid || loadingAcceptInvitation}
							label={t('CreatePassword')}
							size="auto"
						/>
					</div>
				</div>
			</form>
		</div>
	);
};

export default AcceptInvitationForm;
